import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  REGISTRATION_REMINDER_BATCH_INTERVAL_MS,
  REGISTRATION_REMINDER_BATCH_SIZE,
  REGISTRATION_REMINDER_DEFAULT_HEADING,
  REGISTRATION_REMINDER_DEFAULT_MESSAGE,
  REGISTRATION_REMINDER_MAX_BATCH_INTERVAL_MS,
  REGISTRATION_REMINDER_MIN_BATCH_INTERVAL_MS,
  RegistrationReminderPreviewDto,
  RegistrationReminderSendResultDto,
} from '@ivy-monorepo/shared'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import NotificationDevicePreview from '../../components/admin/NotificationDevicePreview'
import { adminService } from '../../services/admin.service'

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return 'Never'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Never'
  }
  return date.toLocaleString()
}

const defaultIntervalSeconds = Math.round(REGISTRATION_REMINDER_BATCH_INTERVAL_MS / 1000)

const NotificationsReEngageTab = () => {
  const [preview, setPreview] = useState<RegistrationReminderPreviewDto | null>(null)
  const [message, setMessage] = useState(REGISTRATION_REMINDER_DEFAULT_MESSAGE)
  const [limitInput, setLimitInput] = useState('')
  const [intervalSecondsInput, setIntervalSecondsInput] = useState(String(defaultIntervalSeconds))
  const [error, setError] = useState('')
  const [sendResult, setSendResult] = useState<RegistrationReminderSendResultDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const loadPreview = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const data = await adminService.getRegistrationReminderPreview()
      setPreview(data)
      setMessage(prev => {
        const trimmed = prev.trim()
        if (!trimmed || trimmed === REGISTRATION_REMINDER_DEFAULT_MESSAGE) {
          return data.defaultMessage
        }
        return prev
      })
      setIntervalSecondsInput(prev => {
        if (!prev.trim() || prev === String(defaultIntervalSeconds)) {
          return String(Math.round(data.batchIntervalMs / 1000))
        }
        return prev
      })
    } catch {
      setError('Failed to load re-engage audience stats.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPreview()
  }, [loadPreview])

  const handleResetMessage = () => {
    setMessage(preview?.defaultMessage ?? REGISTRATION_REMINDER_DEFAULT_MESSAGE)
  }

  const parsedLimit = useMemo(() => parseOptionalPositiveInt(limitInput), [limitInput])
  const parsedIntervalMs = useMemo(
    () => parseIntervalSecondsToMs(intervalSecondsInput, preview?.batchIntervalMs),
    [intervalSecondsInput, preview?.batchIntervalMs],
  )

  const eligibleCount = preview?.eligibleCount ?? 0
  const sendCount =
    parsedLimit.value != null ? Math.min(parsedLimit.value, eligibleCount) : eligibleCount
  const batchSize = preview?.batchSize ?? REGISTRATION_REMINDER_BATCH_SIZE
  const batchCount = sendCount > 0 ? Math.ceil(sendCount / batchSize) : 0
  const intervalMs = parsedIntervalMs.value ?? REGISTRATION_REMINDER_BATCH_INTERVAL_MS
  const estimatedSeconds = batchCount > 1 ? Math.round(((batchCount - 1) * intervalMs) / 1000) : 0

  const handleConfirmSend = async () => {
    const trimmed = message.trim()
    if (!trimmed) {
      setError('Message cannot be empty.')
      return false
    }
    if (parsedLimit.error) {
      setError(parsedLimit.error)
      return false
    }
    if (parsedIntervalMs.error) {
      setError(parsedIntervalMs.error)
      return false
    }

    setIsSending(true)
    setError('')
    setSendResult(null)
    try {
      const result = await adminService.sendRegistrationReminders({
        message: trimmed,
        limit: parsedLimit.value,
        batchIntervalMs: parsedIntervalMs.value,
      })
      setSendResult(result)
      await loadPreview()
      return true
    } catch {
      setError('Failed to send registration reminders.')
      return false
    } finally {
      setIsSending(false)
    }
  }

  const canSend =
    !isLoading &&
    eligibleCount > 0 &&
    message.trim().length > 0 &&
    sendCount > 0 &&
    !parsedLimit.error &&
    !parsedIntervalMs.error

  return (
    <div className="notifications-reengage-tab">
      <p style={{ marginBottom: '16px', color: '#555' }}>
        Re-engage Pending accounts that started signup but never finished. Sends an{' '}
        <code>OnboardingIncomplete</code> push only to <strong>real</strong> users with a valid
        device token (internal accounts are excluded), in batches of {batchSize} with a pause
        between requests. Then records <code>registrationReminderSentAt</code> for return stats.
      </p>

      {isLoading && !preview ? (
        <div className="loader-wrapper">
          <div className="loader" />
        </div>
      ) : null}

      {error ? (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Audience stats</h3>
        <button
          type="button"
          className="styled-button sm"
          disabled={isLoading || isSending}
          onClick={() => void loadPreview()}
        >
          {isLoading ? 'Refreshing…' : 'Refresh stats'}
        </button>
      </div>

      {preview ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          <StatCard label="Pending accounts" value={preview.pendingTotal} />
          <StatCard label="Push-eligible (real)" value={preview.eligibleCount} />
          <StatCard label="Internal excluded" value={preview.excludedInternalCount} />
          <StatCard label="Never reminded" value={preview.neverRemindedCount} />
          <StatCard label="Already reminded" value={preview.alreadyRemindedCount} />
          <StatCard label="Returned (30 days)" value={preview.returnedLast30Days} />
          <StatCard label="Last reminder" value={formatDateTime(preview.lastReminderSentAt)} />
        </div>
      ) : null}

      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label htmlFor="registration-reminder-message" style={{ fontWeight: 600 }}>
            Message
          </label>
          <button type="button" className="link-button" style={{ marginLeft: 0 }} onClick={handleResetMessage}>
            Reset to default
          </button>
        </div>
        <textarea
          id="registration-reminder-message"
          value={message}
          onChange={event => setMessage(event.target.value)}
          rows={4}
          style={{ width: '100%', padding: '10px', resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>

      <NotificationDevicePreview
        message={message}
        heading={preview?.defaultHeading ?? REGISTRATION_REMINDER_DEFAULT_HEADING}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <div>
          <label htmlFor="registration-reminder-limit" style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
            Max recipients (optional)
          </label>
          <input
            id="registration-reminder-limit"
            type="number"
            min={1}
            step={1}
            value={limitInput}
            onChange={event => setLimitInput(event.target.value)}
            placeholder={`All ${eligibleCount} eligible`}
            style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
            disabled={isSending}
          />
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
            Leave empty to send to everyone eligible. Never-reminded users are sent first.
          </div>
        </div>
        <div>
          <label
            htmlFor="registration-reminder-interval"
            style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}
          >
            Pause between batches (seconds)
          </label>
          <input
            id="registration-reminder-interval"
            type="number"
            min={REGISTRATION_REMINDER_MIN_BATCH_INTERVAL_MS / 1000}
            max={REGISTRATION_REMINDER_MAX_BATCH_INTERVAL_MS / 1000}
            step={1}
            value={intervalSecondsInput}
            onChange={event => setIntervalSecondsInput(event.target.value)}
            style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
            disabled={isSending}
          />
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
            Default {defaultIntervalSeconds}s. Allowed{' '}
            {REGISTRATION_REMINDER_MIN_BATCH_INTERVAL_MS / 1000}–
            {REGISTRATION_REMINDER_MAX_BATCH_INTERVAL_MS / 1000}s.
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#555' }}>
        This send: <strong>{sendCount}</strong> of {eligibleCount} real user
        {eligibleCount === 1 ? '' : 's'} · {batchCount} batch{batchCount === 1 ? '' : 'es'} of up to{' '}
        {batchSize}
        {estimatedSeconds > 0 ? ` · ~${estimatedSeconds}s of pauses` : ''}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <button
          type="button"
          className="styled-button"
          disabled={!canSend || isSending}
          onClick={() => setIsConfirmOpen(true)}
        >
          Send re-engage push
        </button>
      </div>

      {sendResult ? (
        <div style={{ marginBottom: '16px', color: '#2e7d32' }}>
          Sent to {sendResult.sent} of {sendResult.eligible} eligible Pending users
          {sendResult.batchCount > 0
            ? ` in ${sendResult.batchCount} batch${sendResult.batchCount === 1 ? '' : 'es'}`
            : ''}
          {sendResult.limit != null ? ` (limit ${sendResult.limit})` : ''}.
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => {
          if (!isSending) {
            setIsConfirmOpen(false)
          }
        }}
        onConfirm={handleConfirmSend}
        title="Send re-engage notifications?"
        confirmLabel="Send"
        isLoading={isSending}
      >
        <p>
          This will send a push to <strong>{sendCount}</strong> real Pending user
          {sendCount === 1 ? '' : 's'} with a valid device token
          {sendCount !== eligibleCount ? ` (capped from ${eligibleCount} eligible)` : ''}.
        </p>
        <p style={{ marginTop: '12px' }}>
          Delivery is staggered: {batchCount} batch{batchCount === 1 ? '' : 'es'} of up to {batchSize},
          with {intervalMs / 1000}s between batches.
        </p>
        <p style={{ marginTop: '12px', whiteSpace: 'pre-wrap' }}>
          Message:
          {'\n'}
          {message.trim()}
        </p>
      </ConfirmationModal>
    </div>
  )
}

const StatCard = ({ label, value }: { label: string; value: string | number }) => (
  <div
    style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '12px 14px',
      background: '#fafafa',
    }}
  >
    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '20px', fontWeight: 600 }}>{value}</div>
  </div>
)

function parseOptionalPositiveInt(raw: string): { value?: number; error?: string } {
  const trimmed = raw.trim()
  if (!trimmed) {
    return {}
  }
  const parsed = Number(trimmed)
  if (!Number.isInteger(parsed) || parsed < 1) {
    return { error: 'Max recipients must be a positive integer.' }
  }
  return { value: parsed }
}

function parseIntervalSecondsToMs(
  raw: string,
  fallbackMs: number | undefined,
): { value?: number; error?: string } {
  const trimmed = raw.trim()
  if (!trimmed) {
    return { value: fallbackMs ?? REGISTRATION_REMINDER_BATCH_INTERVAL_MS }
  }
  const seconds = Number(trimmed)
  if (!Number.isInteger(seconds)) {
    return { error: 'Pause between batches must be a whole number of seconds.' }
  }
  const ms = seconds * 1000
  if (ms < REGISTRATION_REMINDER_MIN_BATCH_INTERVAL_MS || ms > REGISTRATION_REMINDER_MAX_BATCH_INTERVAL_MS) {
    return {
      error: `Pause between batches must be between ${REGISTRATION_REMINDER_MIN_BATCH_INTERVAL_MS / 1000} and ${REGISTRATION_REMINDER_MAX_BATCH_INTERVAL_MS / 1000} seconds.`,
    }
  }
  return { value: ms }
}

export default NotificationsReEngageTab
